import { useMemo } from 'react';
import { AppType, useAppContext } from '@features/contexts/AppProvider';
import {
  ChannelFilters,
  StreamChatFiltersResult,
  UseStreamChatFiltersParams,
} from '@features/components/Chat/types/streamChatFilters';
import { hasChatsFullAccess } from '@features/components/Chat/utils';
import { CONNECT_CHAT_TYPES, CP_CHAT_TYPES, OPO_CHAT_TYPES } from '@features/components/Chat/constants';

export const getMembershipFilters = (userId: string, caseId?: string): ChannelFilters => {
  const caseIdFilter = caseId ? { caseIds: { $contains: caseId.toLowerCase() } } : {};

  return {
    type: { $in: CONNECT_CHAT_TYPES },
    members: { $in: [userId] },
    ...caseIdFilter,
  };
};

const getLPChatFilters = ({
  userId,
  caseId,
  userRole,
  allowedTypes,
}: UseStreamChatFiltersParams): StreamChatFiltersResult => {
  const membershipFilters = getMembershipFilters(userId, caseId);

  const hasFullAccess = hasChatsFullAccess(userRole);

  if (!hasFullAccess || !allowedTypes?.length) {
    return { filters: membershipFilters, chatTypes: CONNECT_CHAT_TYPES };
  }

  const caseIdFilter = caseId ? { caseIds: { $contains: caseId.toLowerCase() } } : {};

  const fullAccessFilters = {
    type: { $in: allowedTypes },
    ...caseIdFilter,
  };

  return {
    filters: { $or: [fullAccessFilters, membershipFilters] },
    chatTypes: CONNECT_CHAT_TYPES,
  };
};

const getCPChatFilters = ({ userId, caseId, txCenterId }: UseStreamChatFiltersParams): StreamChatFiltersResult => {
  if (!caseId || !txCenterId) {
    return { filters: null, chatTypes: [] };
  }
  return {
    filters: {
      type: { $in: CP_CHAT_TYPES },
      members: { $in: [userId] },
      caseIds: { $contains: caseId.toLowerCase() },
      txcenter_id: { $in: [txCenterId] },
    },
    chatTypes: CP_CHAT_TYPES,
  };
};

const getOpoChatFilters = ({ userId, caseId }: UseStreamChatFiltersParams): StreamChatFiltersResult => {
  const base: ChannelFilters = {
    type: { $in: OPO_CHAT_TYPES },
    members: { $in: [userId] },
  };
  const filters = caseId ? { ...base, caseIds: { $contains: caseId.toLowerCase() } } : base;
  return { filters, chatTypes: OPO_CHAT_TYPES };
};

export function useStreamChatFilters({
  caseId,
  userId,
  userRole,
  allowedTypes,
  txCenterId,
  isOpoMode,
}: UseStreamChatFiltersParams): StreamChatFiltersResult {
  const { type } = useAppContext();

  return useMemo(() => {
    if (type === AppType.NopConnectPlatform) {
      return getLPChatFilters({ userId, caseId, userRole, allowedTypes });
    }

    if (isOpoMode) {
      return getOpoChatFilters({ userId, caseId });
    }

    return getCPChatFilters({ userId, caseId, txCenterId });
  }, [type, caseId, userId, userRole, allowedTypes, txCenterId, isOpoMode]);
}
